import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, DollarSign, Download, RefreshCw, Ban, Loader2, X, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";

const STATUS_TONES = {
  pending:  { bg: "bg-amber-100",   fg: "text-amber-900",   label: "Pending" },
  payable:  { bg: "bg-sky-100",     fg: "text-sky-900",     label: "Payable" },
  paid:     { bg: "bg-emerald-100", fg: "text-emerald-900", label: "Paid" },
  void:     { bg: "bg-slate-200",   fg: "text-slate-700",   label: "Void" },
  reversed: { bg: "bg-rose-100",    fg: "text-rose-900",    label: "Reversed" },
};
const NEXT_STATUSES = ["pending", "payable", "paid", "void", "reversed"];
const PRICING_MODES = [
  { key: "national_partner", label: "National partner" },
  { key: "local_scwt",       label: "Local Aware" },
  { key: "admin_override",   label: "Admin override" },
];
// Feb 2026 — the three PWS-eligible test packages. `test_id` filters
// via a cheap booking-lookup on the backend (see
// admin_commissions._build_ledger_query). Legacy `town_water` option
// was removed — no such test_id exists so it always yielded an empty
// ledger filter.
const TEST_PACKAGES = [
  { key: "basic", label: "AWARE 4-Point" },
  { key: "tank",  label: "AWARE Tank 360" },
  { key: "bore",  label: "AWARE Bore 360" },
];

function fmtCurrency(n) {
  if (n == null) return "—";
  return `$${Number(n).toFixed(2)}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU"); } catch { return "—"; }
}

export default function AdminPWSCommissions() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [partnerFilter, setPartnerFilter] = useState("");
  const [bookingFilter, setBookingFilter] = useState(searchParams.get("booking") || "");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [pricingModeFilter, setPricingModeFilter] = useState("");
  const [testIdFilter, setTestIdFilter] = useState("");
  const [busy, setBusy] = useState(null);
  const [voidModalOpen, setVoidModalOpen] = useState(false);

  const buildParams = () => {
    const params = {};
    if (statusFilter)       params.status            = statusFilter;
    if (partnerFilter.trim())  params.partner_id     = partnerFilter.trim();
    if (bookingFilter.trim())  params.booking_reference = bookingFilter.trim();
    if (dateFromFilter)     params.date_from         = dateFromFilter;
    if (dateToFilter)       params.date_to           = dateToFilter;
    if (pricingModeFilter)  params.pricing_mode      = pricingModeFilter;
    if (testIdFilter)       params.test_id           = testIdFilter;
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: listData }, { data: sumData }] = await Promise.all([
        api.get("/admin/pws-commissions", { params: buildParams() }),
        api.get("/admin/pws-commissions/summary"),
      ]);
      setRows(listData.rows || []);
      setSummary(sumData);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load commissions");
    } finally {
      setLoading(false);
    }
  };

  // Initial + status-change load. Re-runs whenever the status quick
  // filter flips so the summary tile changes feel snappy without a
  // second click. `bookingFilter` is intentionally omitted from the
  // deps: it's only consulted once, on the initial load, so a
  // pre-filtered deep-link (`?booking=…`) lands correctly without
  // making every keystroke in the booking-filter input re-fetch the
  // whole list. The explicit Refresh button covers manual reloads.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = {};
        if (statusFilter) params.status = statusFilter;
        if (bookingFilter.trim()) params.booking_reference = bookingFilter.trim();
        const [{ data: listData }, { data: sumData }] = await Promise.all([
          api.get("/admin/pws-commissions", { params }),
          api.get("/admin/pws-commissions/summary"),
        ]);
        if (cancelled) return;
        setRows(listData.rows || []);
        setSummary(sumData);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Failed to load commissions");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [statusFilter]);

  // Keep the URL query in sync with the booking filter so admins can
  // share a deep-link back to a filtered view. `searchParams` is
  // omitted from deps intentionally — including it would cause an
  // infinite loop with `setSearchParams` below.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (bookingFilter.trim()) next.set("booking", bookingFilter.trim());
    else next.delete("booking");
    setSearchParams(next, { replace: true });
  }, [bookingFilter]);

  const setStatus = async (row, status) => {
    setBusy(row.ledger_id);
    try {
      const reason = status === "paid"
        ? (window.prompt("Reference / EFT note (optional):") || "")
        : (status === "reversed"
            ? (window.prompt("Reversal reason:") || "")
            : (status === "void"
                ? (window.prompt("Void reason (optional):") || "")
                : ""));
      await api.patch(`/admin/pws-commissions/${row.ledger_id}/status`,
                       { status, reason: reason || null });
      toast.success(`Marked ${status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status update failed");
    } finally {
      setBusy(null);
    }
  };

  // ── CSV export (Feb 2026 hand-back) ────────────────────────────
  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const params = buildParams();
      // Use api's baseURL for absolute file download so browser
      // triggers a native save dialog rather than a JSON preview.
      const resp = await api.get("/admin/pws-commissions.csv", {
        params,
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Prefer the server-supplied filename if present.
      const cd = resp.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      a.download = match?.[1] || `scwt-commissions-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CSV export failed");
    } finally {
      setDownloading(false);
    }
  };

  const totalsPanel = useMemo(() => {
    if (!summary) return null;
    const entries = ["pending", "payable", "paid", "void", "reversed"].map(s => ({
      status: s,
      count: summary.by_status?.[s]?.count || 0,
      amount: summary.by_status?.[s]?.amount || 0,
    }));
    return entries;
  }, [summary]);

  const clearAllFilters = () => {
    setStatusFilter("");
    setPartnerFilter("");
    setBookingFilter("");
    setDateFromFilter("");
    setDateToFilter("");
    setPricingModeFilter("");
    setTestIdFilter("");
  };

  return (
    <PortalShell>
      <div data-testid="admin-pws-commissions-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/admin/partners/network"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-700 hover:text-cyan-800"
          data-testid="back-to-pws-network-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to PWS Network
        </Link>

        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">
              Aware Partner Network · Phase 10
            </div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">
              Commission Ledger
            </h1>
            <p className="text-slate-500 mt-1 text-sm max-w-2xl">
              Ledger entries are created automatically when a national-partner booking is paid.
              This view is read-only for the partner; admins can mutate status here.
              No money moves from this screen — Phase 10 is a ledger only.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={downloadCsv}
              disabled={downloading}
              data-testid="admin-pws-commissions-csv-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 shadow-sm"
            >
              <Download className="w-3.5 h-3.5" />
              {downloading ? "Preparing…" : "Download CSV"}
            </button>
            <button
              type="button"
              onClick={() => setVoidModalOpen(true)}
              data-testid="admin-pws-commissions-void-non-partner-link-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
              title="Void ledger rows whose booking wasn't via a fresh partner link (stored attribution / region allocation). Never touches paid rows."
            >
              <Ban className="w-3.5 h-3.5" />
              Void non-partner-link
            </button>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="admin-pws-commissions-refresh-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-60"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
          </div>
        </div>

        {/* Summary chips */}
        {totalsPanel && (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="admin-pws-commissions-summary">
            {totalsPanel.map(e => {
              const tone = STATUS_TONES[e.status] || STATUS_TONES.pending;
              return (
                <div
                  key={e.status}
                  data-testid={`admin-pws-commissions-summary-${e.status}`}
                  className={`rounded-xl p-3 ${tone.bg} ${tone.fg}`}
                >
                  <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{tone.label}</div>
                  <div className="mt-1 text-lg font-bold">{fmtCurrency(e.amount)}</div>
                  <div className="text-[11px] opacity-75">{e.count} entr{e.count === 1 ? "y" : "ies"}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Filters */}
        <div className="mt-6 grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-6 items-end">
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="admin-pws-commissions-status-filter"
              className="text-sm px-2 py-1.5 rounded-md border border-slate-200 bg-white w-full"
            >
              <option value="">All statuses</option>
              {NEXT_STATUSES.map(s => <option key={s} value={s}>{STATUS_TONES[s].label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Partner ID</label>
            <input
              value={partnerFilter}
              onChange={(e) => setPartnerFilter(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              placeholder="filter by partner_id"
              data-testid="admin-pws-commissions-partner-filter"
              className="text-sm px-2 py-1.5 rounded-md border border-slate-200 bg-white w-full"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Booking</label>
            <input
              value={bookingFilter}
              onChange={(e) => setBookingFilter(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              placeholder="SCWT-XXXXXX"
              data-testid="admin-pws-commissions-booking-filter"
              className="text-sm px-2 py-1.5 rounded-md border border-slate-200 bg-white w-full"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Date from</label>
            <input
              type="date"
              value={dateFromFilter}
              onChange={(e) => setDateFromFilter(e.target.value)}
              data-testid="admin-pws-commissions-date-from-filter"
              className="text-sm px-2 py-1.5 rounded-md border border-slate-200 bg-white w-full"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Date to</label>
            <input
              type="date"
              value={dateToFilter}
              onChange={(e) => setDateToFilter(e.target.value)}
              data-testid="admin-pws-commissions-date-to-filter"
              className="text-sm px-2 py-1.5 rounded-md border border-slate-200 bg-white w-full"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Pricing mode</label>
            <select
              value={pricingModeFilter}
              onChange={(e) => setPricingModeFilter(e.target.value)}
              data-testid="admin-pws-commissions-pricing-mode-filter"
              className="text-sm px-2 py-1.5 rounded-md border border-slate-200 bg-white w-full"
            >
              <option value="">All modes</option>
              {PRICING_MODES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Test package</label>
            <select
              value={testIdFilter}
              onChange={(e) => setTestIdFilter(e.target.value)}
              data-testid="admin-pws-commissions-test-id-filter"
              className="text-sm px-2 py-1.5 rounded-md border border-slate-200 bg-white w-full"
            >
              <option value="">All packages</option>
              {TEST_PACKAGES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2 md:col-span-2 lg:col-span-1">
            <button
              type="button"
              onClick={load}
              data-testid="admin-pws-commissions-apply-filters-btn"
              className="text-xs font-semibold px-3 py-1.5 rounded-md bg-cyan-600 text-white hover:bg-cyan-700"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={() => { clearAllFilters(); }}
              data-testid="admin-pws-commissions-clear-filters-btn"
              className="text-xs font-semibold px-3 py-1.5 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200"
            >
              Clear
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="mt-4 rounded-xl bg-white border border-slate-200 overflow-hidden">
          {loading ? (
            <div data-testid="admin-pws-commissions-loading" className="p-10 text-center text-slate-500 text-sm">Loading…</div>
          ) : rows.length === 0 ? (
            <div data-testid="admin-pws-commissions-empty" className="p-10 text-center">
              <DollarSign className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <div className="text-sm text-slate-600">No commission entries yet.</div>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="admin-pws-commissions-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="text-left px-4 py-3 font-bold">Booking</th>
                  <th className="text-left px-4 py-3 font-bold">Partner</th>
                  <th className="text-left px-4 py-3 font-bold">Pricing</th>
                  <th className="text-right px-4 py-3 font-bold">Booking ($)</th>
                  <th className="text-right px-4 py-3 font-bold">Margin ($)</th>
                  <th className="text-right px-4 py-3 font-bold">Commission</th>
                  <th className="text-left px-4 py-3 font-bold">Status</th>
                  <th className="text-left px-4 py-3 font-bold">Created</th>
                  <th className="text-right px-4 py-3 font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const tone = STATUS_TONES[row.commission_status] || STATUS_TONES.pending;
                  return (
                    <tr
                      key={row.ledger_id}
                      data-testid={`admin-pws-commissions-row-${row.booking_reference}`}
                      className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50"
                    >
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/bookings/${row.booking_reference}`}
                          className="font-mono text-xs text-cyan-700 hover:underline"
                          data-testid="admin-pws-commissions-booking-link"
                        >
                          {row.booking_reference}
                        </Link>
                        <div className="text-[10px] text-slate-500 mt-0.5">{row.commission_source || row.price_version || "—"}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-slate-900">{row.partner_name || "—"}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{row.partner_slug}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-900 font-bold uppercase tracking-wider">
                          {(row.pricing_mode || "—").replaceAll("_", " ")}
                        </span>
                        {row.commission_basis && (
                          <div className="text-[10px] text-slate-500 mt-1">basis: {row.commission_basis}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-700">{fmtCurrency(row.final_price)}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{fmtCurrency(row.partner_margin_allowance)}</td>
                      <td
                        className="px-4 py-3 text-right font-bold text-slate-900"
                        data-testid={`admin-pws-commissions-amount-${row.booking_reference}`}
                      >
                        {fmtCurrency(row.commission_amount)}
                        {row.commission_cap_applied && (
                          <div className="text-[9px] font-normal text-amber-700">cap applied</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          data-testid={`admin-pws-commissions-status-${row.booking_reference}`}
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${tone.bg} ${tone.fg}`}
                        >
                          {tone.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-700 text-xs">{fmtDate(row.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <select
                          value={row.commission_status}
                          disabled={busy === row.ledger_id}
                          onChange={(e) => {
                            const next = e.target.value;
                            if (next === row.commission_status) return;
                            setStatus(row, next);
                          }}
                          data-testid={`admin-pws-commissions-status-select-${row.booking_reference}`}
                          className="text-[11px] font-semibold px-2 py-1 rounded-md border border-slate-200 bg-white"
                        >
                          {NEXT_STATUSES.map(s => (
                            <option key={s} value={s}>{STATUS_TONES[s].label}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-4 text-xs text-slate-500" data-testid="admin-pws-commissions-disclaimer">
          Commission Ledger is read-only for partners. Status changes are audit-logged.
          Phase 10 does not auto-payout — money movement is handled out-of-band.
          CSV export streams all filtered rows in one file.
        </div>
      </div>
      {voidModalOpen && (
        <VoidNonPartnerLinkModal
          onClose={() => setVoidModalOpen(false)}
          onApplied={() => { setVoidModalOpen(false); load(); }}
        />
      )}
    </PortalShell>
  );
}


/**
 * Feb 2026 — one-shot admin housekeeping modal that voids
 * commission_ledger rows for bookings that were NOT captured via a
 * fresh partner-link click. Server-side handler:
 *   POST /api/admin/pws-commissions/void-non-partner-link-bookings
 *
 * Never touches paid / already-void / reversed rows. Dry-run mode
 * previews before applying.
 */
function VoidNonPartnerLinkModal({ onClose, onApplied }) {
  const [phase, setPhase] = useState("loading"); // loading | preview | applying | error
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const runDryRun = async () => {
    setPhase("loading");
    setError("");
    try {
      const res = await api.post(
        "/admin/pws-commissions/void-non-partner-link-bookings?dry_run=true",
      );
      setPreview(res.data);
      setPhase("preview");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Preview failed");
      setPhase("error");
    }
  };

  useEffect(() => { runDryRun(); }, []);

  const apply = async () => {
    setPhase("applying");
    setError("");
    try {
      const res = await api.post(
        "/admin/pws-commissions/void-non-partner-link-bookings",
      );
      const n = res?.data?.voided ?? 0;
      toast.success(n === 0
        ? "Nothing to void — all payable rows are legitimate partner-link bookings."
        : `Voided ${n} commission row${n === 1 ? "" : "s"}.`);
      onApplied?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Apply failed");
      setPhase("error");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="admin-pws-void-non-partner-link-modal"
    >
      <div className="bg-white rounded-3xl max-w-2xl w-full p-6 max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2">
              <Ban className="w-4 h-4 text-rose-600" />
              Void non-partner-link commissions
            </h2>
            <p className="text-xs text-slate-500 mt-1 max-w-md">
              Marks commission rows as <code className="text-[10px]">void</code> when
              their booking was attributed via cookie / stored attribution or region
              auto-allocation instead of a fresh partner link. Never touches paid,
              already-void or reversed rows.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="admin-pws-void-modal-close"
            className="p-1.5 hover:bg-slate-100 rounded-lg"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {phase === "loading" && (
          <div className="text-slate-400 flex items-center gap-2 py-6" data-testid="admin-pws-void-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Scanning ledger…
          </div>
        )}

        {phase === "error" && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 flex items-start gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold">Failed</div>
              <div className="text-xs mt-0.5 break-all">{typeof error === "string" ? error : JSON.stringify(error)}</div>
            </div>
            <button
              onClick={runDryRun}
              className="text-xs font-semibold underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        )}

        {(phase === "preview" || phase === "applying") && preview && (
          <>
            <div className="grid grid-cols-3 gap-2 mb-4" data-testid="admin-pws-void-summary">
              <SummaryTile label="Scanned" value={preview.scanned} />
              <SummaryTile label="To void" value={preview.voided} accent="rose" />
              <SummaryTile label="Preserved (paid / partner-link)" value={preview.skipped_ineligible} accent="emerald" />
            </div>

            {preview.voided === 0 ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-start gap-2" data-testid="admin-pws-void-clean">
                <CheckCircle2 className="w-4 h-4 mt-0.5" />
                No non-partner-link rows to void. Ledger already reflects the strict rule.
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-200 overflow-hidden" data-testid="admin-pws-void-preview-list">
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-bold">Booking</th>
                        <th className="text-left px-3 py-2 font-bold">Source seen</th>
                        <th className="text-left px-3 py-2 font-bold">Prev. status</th>
                        <th className="text-right px-3 py-2 font-bold">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {preview.results.map((r) => (
                        <tr key={r.ledger_id} data-testid={`admin-pws-void-row-${r.ledger_id}`}>
                          <td className="px-3 py-2 font-semibold text-slate-800 truncate max-w-[160px]">{r.booking_reference}</td>
                          <td className="px-3 py-2 text-slate-500">
                            <code className="text-[10px]">{r.booking_source || "—"}</code>
                          </td>
                          <td className="px-3 py-2 text-slate-500">{r.previous_status}</td>
                          <td className="px-3 py-2 text-right font-mono text-slate-700">
                            ${(Number(r.commission_amount) || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="admin-pws-void-cancel"
            className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900"
          >
            Close
          </button>
          {phase === "preview" && preview?.voided > 0 && (
            <button
              onClick={apply}
              data-testid="admin-pws-void-apply"
              className="px-4 py-1.5 rounded-full text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white inline-flex items-center gap-1.5"
            >
              <Ban className="w-3.5 h-3.5" />
              Void {preview.voided} row{preview.voided === 1 ? "" : "s"}
            </button>
          )}
          {phase === "applying" && (
            <button
              disabled
              data-testid="admin-pws-void-applying"
              className="px-4 py-1.5 rounded-full text-xs font-bold bg-rose-400 text-white inline-flex items-center gap-1.5"
            >
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Voiding…
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function SummaryTile({ label, value, accent }) {
  const bg = accent === "rose"
    ? "bg-rose-50 border-rose-200 text-rose-800"
    : accent === "emerald"
    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
    : "bg-slate-50 border-slate-200 text-slate-700";
  return (
    <div className={`rounded-xl border p-2.5 ${bg}`}>
      <div className="text-[9px] uppercase tracking-wider font-bold opacity-70">{label}</div>
      <div className="text-lg font-black leading-tight mt-0.5">{value}</div>
    </div>
  );
}

